import { Injectable } from '@angular/core';
import { Observable, catchError, forkJoin, map, of, tap } from 'rxjs';
import { ApiService } from './api.service';
import { AppStateService } from './app-state.service';
import { TicketTransaction, UserProfile, UserStats } from '../../models';

const PROFILES_KEY = 'cinephile_profiles';

type AnyRecord = any;

@Injectable({ providedIn: 'root' })
export class UserService {
  private avatars = ['🎬', '🎥', '🎞️', '🍿', '🎭', '📽️'];

  constructor(
    private api: ApiService,
    private state: AppStateService
  ) {}

  createUser(userId: string, displayName: string, avatar = '🎬'): Observable<UserProfile> {
    const profile = this.buildProfile(userId, displayName, avatar);
    this.saveProfile(profile);
    this.state.setUser(profile);

    return this.api.post<any>('/api/users/', {
      user_id: userId,
      display_name: displayName,
      avatar: avatar
    }).pipe(
      map(() => profile),
      catchError(() => of(profile))
    );
  }

  getUser(userId: string): Observable<UserProfile> {
    const local = this.getSavedProfile(userId);
    return this.api.get<any>(`/api/users/${userId}`).pipe(
      map((doc) => {
        const p: UserProfile = {
          id: String(doc?.id ?? doc?._id ?? userId),
          user_id: String(doc?.user_id ?? userId),
          display_name: String(doc?.display_name || local?.display_name || this.displayNameFromId(userId)),
          avatar: String(doc?.avatar || local?.avatar || '🎬'),
          tickets: this.readNumber(doc?.tickets, local?.tickets ?? 0),
          total_tickets_earned: this.readNumber(doc?.total_tickets_earned, local?.total_tickets_earned ?? 0),
          total_score: this.readNumber(doc?.total_score, local?.total_score ?? 0),
          quizzes_completed: this.readNumber(doc?.quizzes_completed, local?.quizzes_completed ?? 0),
          correct_answers: this.readNumber(doc?.correct_answers, local?.correct_answers ?? 0),
          total_questions_answered: this.readNumber(doc?.total_questions_answered, local?.total_questions_answered ?? 0),
          current_streak: this.readNumber(doc?.current_streak, local?.current_streak ?? 0),
          best_streak: this.readNumber(doc?.best_streak, local?.best_streak ?? 0),
          producer_title: doc?.producer_title || local?.producer_title || 'Extra',
          unlocked_posters: this.readArray(doc?.unlocked_posters, local?.unlocked_posters ?? []),
          created_at: String(doc?.created_at || local?.created_at || new Date().toISOString())
        };
        this.saveProfile(p);
        this.state.setUser(p);
        return p;
      }),
      catchError(() => {
        const profile = local ?? this.buildProfile(userId, this.displayNameFromId(userId), this.pickAvatar(userId));
        this.saveProfile(profile);
        this.state.setUser(profile);
        return of(profile);
      })
    );
  }

  getStats(userId: string): Observable<UserStats> {
    return this.api.get<any>(`/api/users/${userId}/stats`).pipe(
      map((raw) => {
        const local = this.getSavedProfile(userId);
        const displayName = String(raw?.display_name || local?.display_name || this.displayNameFromId(userId));
        const avatar = String(raw?.avatar || local?.avatar || '🎬');
        const producerTitle = raw?.producer_title || local?.producer_title || 'Extra';
        const tickets = this.readNumber(raw?.tickets, local?.tickets ?? 0);
        const currentStreak = Math.max(this.readNumber(raw?.current_streak, 0), local?.current_streak ?? 0);
        const bestStreak = Math.max(this.readNumber(raw?.best_streak, 0), currentStreak, local?.best_streak ?? 0);
        const globalRank = Math.max(1, this.readNumber(raw?.global_rank, 1));
        const accuracy = this.readNumber(raw?.accuracy, 0);

        const stats: UserStats = {
          user_id: String(raw?.user_id || userId),
          display_name: displayName,
          avatar: avatar,
          producer_title: producerTitle,
          tickets: tickets,
          total_score: this.readNumber(raw?.total_score, 0),
          quizzes_completed: this.readNumber(raw?.quizzes_completed, 0),
          current_streak: currentStreak,
          best_streak: bestStreak,
          global_rank: globalRank,
          accuracy: accuracy,
          unlocked_poster_count: this.readNumber(raw?.unlocked_poster_count, local?.unlocked_posters?.length ?? 0)
        };

        if (local) {
          const updated: UserProfile = {
            ...local,
            tickets: Math.max(local.tickets, tickets),
            current_streak: currentStreak,
            best_streak: bestStreak,
            total_score: stats.total_score,
            quizzes_completed: stats.quizzes_completed
          };
          this.saveProfile(updated);
          this.state.setUser(updated);
        }

        return stats;
      }),
      catchError(() => {
        return forkJoin({
          user: this.getUser(userId),
          attempts: this.api
            .get<any>(`/api/quiz/users/${userId}/attempts`, { skip: 0, limit: 200 })
            .pipe(catchError(() => of([]))),
          rank: this.api
            .get<any>(`/api/leaderboard/user/${userId}`)
            .pipe(catchError(() => of(null)))
        }).pipe(
          map(({ user, attempts, rank }) => this.buildStats(user, attempts, rank))
        );
      })
    );
  }

  getTicketHistory(userId: string, limit = 20): Observable<{ current_balance: number; transactions: TicketTransaction[] }> {
    return this.getUser(userId).pipe(
      map((user) => ({
        current_balance: user.tickets,
        transactions: this.readTransactions(user).slice(0, limit)
      }))
    );
  }

  loadCurrentUser(): Observable<UserProfile> {
    return this.getUser(this.state.userId);
  }

  private buildStats(user: UserProfile, attemptsRaw: unknown, rankRaw: AnyRecord | null): UserStats {
    const attempts = this.readArray(attemptsRaw, ['attempts', 'results', 'data']);
    const quizzesCompleted = Math.max(attempts.length, user.quizzes_completed || 0);

    let totalQuestions = 0;
    let correctAnswers = 0;
    let scoreTotal = 0;
    let earnedTickets = 0;
    const playedDates = new Set<string>();

    for (const attempt of attempts) {
      const total = this.readNumber(attempt?.['total_questions'], 0);
      const correct = this.readNumber(attempt?.['correct_answers'], 0);
      const score = this.readNumber(attempt?.['score'], this.readNumber(attempt?.['score_pct'], 0));
      const tickets = this.readNumber(attempt?.['tickets_earned'], 0);

      const dStr = attempt?.['completed_at'] || attempt?.['started_at'] || attempt?.['played_at'];
      if (dStr) {
        try {
          playedDates.add(new Date(dStr).toDateString());
        } catch (_) {}
      }

      totalQuestions += total;
      correctAnswers += correct;
      scoreTotal += score;
      earnedTickets += tickets;
    }

    let computedStreak = user.current_streak || 0;
    if (playedDates.size > 0) {
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);

      let count = 0;
      let check = playedDates.has(today.toDateString())
        ? new Date(today)
        : (playedDates.has(yesterday.toDateString()) ? new Date(yesterday) : null);

      if (check) {
        while (playedDates.has(check.toDateString())) {
          count++;
          check.setDate(check.getDate() - 1);
        }
      }
      computedStreak = Math.max(computedStreak, count, 1);
    } else if (quizzesCompleted > 0) {
      computedStreak = Math.max(computedStreak, 1);
    }

    const accuracy = totalQuestions > 0 ? Number(((correctAnswers / totalQuestions) * 100).toFixed(1)) : 0;
    const rank = Math.max(1, this.readNumber(rankRaw?.['rank'], 1));
    const mergedTickets = Math.max(user.tickets, earnedTickets);

    if (mergedTickets !== user.tickets || computedStreak !== user.current_streak) {
      const updatedUser = {
        ...user,
        tickets: mergedTickets,
        total_tickets_earned: Math.max(user.total_tickets_earned, mergedTickets),
        current_streak: computedStreak,
        best_streak: Math.max(user.best_streak, computedStreak)
      };

      this.saveProfile(updatedUser);
      this.state.setUser(updatedUser);
      user = updatedUser;
    }

    return {
      user_id: user.user_id,
      display_name: user.display_name,
      avatar: user.avatar,
      producer_title: user.producer_title,
      tickets: user.tickets,
      total_score: Math.max(Math.round(scoreTotal), user.total_score || 0),
      quizzes_completed: quizzesCompleted,
      current_streak: computedStreak,
      best_streak: Math.max(user.best_streak, computedStreak),
      accuracy,
      global_rank: rank,
      unlocked_poster_count: user.unlocked_posters.length
    };
  }

  private buildProfile(userId: string, displayName: string, avatar: string): UserProfile {
    const now = new Date().toISOString();
    return {
      id: userId,
      user_id: userId,
      display_name: displayName,
      avatar,
      tickets: 0,
      total_tickets_earned: 0,
      total_score: 0,
      quizzes_completed: 0,
      correct_answers: 0,
      total_questions_answered: 0,
      current_streak: 0,
      best_streak: 0,
      producer_title: 'Extra',
      unlocked_posters: [],
      created_at: now
    };
  }

  private displayNameFromId(userId: string): string {
    if (!userId || userId === 'guest_user') return 'Guest Cinephile';
    return userId.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  private pickAvatar(seed: string): string {
    const index = Math.abs(this.hash(seed)) % this.avatars.length;
    return this.avatars[index];
  }

  private hash(value: string): number {
    let out = 0;
    for (let i = 0; i < value.length; i++) {
      out = (out << 5) - out + value.charCodeAt(i);
      out |= 0;
    }
    return out;
  }

  private getSavedProfile(userId: string): UserProfile | null {
    const all = this.readProfiles();
    return (all[userId] as UserProfile) ?? null;
  }

  private saveProfile(profile: UserProfile): void {
    const all = this.readProfiles();
    all[profile.user_id] = profile;
    this.writeProfiles(all);
  }

  private readProfiles(): Record<string, UserProfile> {
    if (typeof localStorage === 'undefined') return {};
    const raw = localStorage.getItem(PROFILES_KEY);
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, UserProfile>) : {};
    } catch {
      return {};
    }
  }

  private writeProfiles(payload: Record<string, UserProfile>): void {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(PROFILES_KEY, JSON.stringify(payload));
  }

  private readTransactions(user: UserProfile): TicketTransaction[] {
    if (user.tickets <= 0) return [];
    return [
      {
        tx_type: 'earn',
        amount: user.tickets,
        description: 'Quiz rewards earned',
        created_at: new Date().toISOString()
      }
    ];
  }

  private readArray<T = any>(value: unknown, fallbackOrKeys?: any): T[] {
    if (Array.isArray(value)) return value as T[];
    if (value && typeof value === 'object' && Array.isArray(fallbackOrKeys)) {
      for (const item of fallbackOrKeys) {
        if (typeof item === 'string') {
          const candidate = (value as AnyRecord)[item];
          if (Array.isArray(candidate)) return candidate as T[];
        }
      }
    }
    if (Array.isArray(fallbackOrKeys)) {
      return fallbackOrKeys as T[];
    }
    return [];
  }

  private readNumber(value: unknown, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
}
